import { useEffect, useState } from "react";

import useHostFooterAction from "components/organisms/CreationModal/hooks/useHostFooterAction";

import {
    mapAutoMatchedToMappings,
    toConversionMappingsPayload,
} from "../DestinationCreation.helpers";

/**
 * Shared LinkedIn CAPI / Microsoft Bing Offline flow: OAuth + account select,
 * save the destination, fetch conversions, then map events to conversions.
 *
 * @param {object} params
 * @param {string} params.code Destination code this instance handles.
 * @param {string[]} params.readyFields `data` fields that must be set to enable Save.
 * @param {string[]} params.requiredFields `data` fields required to save the destination.
 * @param {(data: object) => Promise<object>} params.saveDestination
 * @param {(response: object) => any} params.getDestinationId
 * @param {(destinationId: any) => Promise<object>} params.getConversions
 * @param {(destinationId: any, payload: object) => Promise<object>} params.postMappings
 */
const useConversionMappingDestination = ({
    code,
    readyFields,
    requiredFields,
    saveDestination,
    getDestinationId,
    getConversions,
    postMappings,
    destinationCode,
    language,
    setSaveDisabled,
    setModalLoading,
    completeCreation,
    registerFooterAction,
}) => {
    const [data, setData] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [destinationId, setDestinationId] = useState(null);
    const [conversions, setConversions] = useState([]);
    const [events, setEvents] = useState([]);
    const [autoMatchedEvents, setAutoMatchedEvents] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [isMappingOpen, setIsMappingOpen] = useState(false);
    const [isMappingCompleted, setIsMappingCompleted] = useState(false);

    const readyValues = readyFields.map((field) => data?.[field]);

    useEffect(() => {
        if (
            destinationCode === code &&
            isAuthenticated &&
            readyValues.every(Boolean)
        ) {
            setSaveDisabled(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinationCode, isAuthenticated, ...readyValues]);

    useEffect(() => {
        if (mappings?.length) {
            setSaveDisabled(
                () => !mappings?.every((m) => m.conversion_id && m.event),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mappings, destinationCode]);

    const resetOnTypeChange = () => {
        setData(null);
        setIsAuthenticated(false);
        setDestinationId(null);
        setConversions([]);
        setEvents([]);
        setAutoMatchedEvents([]);
        setMappings([]);
        setIsMappingOpen(false);
        setIsMappingCompleted(false);
    };

    const loadConversions = async (createdId) => {
        try {
            const response = await getConversions(createdId);
            if (response.status && response.data?.conversions) {
                const { all_conversions, all_events, auto_matched_events } =
                    response.data.conversions;
                if (!auto_matched_events?.length) {
                    setSaveDisabled(true);
                }
                setConversions(all_conversions || []);
                setEvents(all_events || []);
                setAutoMatchedEvents(auto_matched_events || []);
                auto_matched_events?.length &&
                    setMappings(mapAutoMatchedToMappings(auto_matched_events));
            }
        } catch (e) {
            // ignore and proceed
        }
    };

    const create = async () => {
        setModalLoading(true);
        if (!requiredFields.every((field) => data?.[field])) {
            setModalLoading(false);
            return;
        }

        const response = await saveDestination(data);
        const createdId = getDestinationId(response);
        if (!response.status || !createdId) {
            setModalLoading(false);
            return;
        }

        setDestinationId(createdId);
        setData(null);
        setIsAuthenticated(false);

        await loadConversions(createdId);

        setIsMappingOpen(true);
        setModalLoading(false);
    };

    const saveMappings = async () => {
        setModalLoading(true);
        try {
            if (destinationId && mappings?.length) {
                const response = await postMappings(
                    destinationId,
                    toConversionMappingsPayload(mappings),
                );
                setIsMappingCompleted(true);
                await completeCreation(destinationId);
                if (!response.status) {
                    setModalLoading(false);
                    return;
                }
            }
        } finally {
            setModalLoading(false);
            setIsMappingOpen(false);
        }
    };

    const isActive = destinationCode === code;
    const canCreate = isAuthenticated && readyValues.every(Boolean);
    const canSaveMappings =
        mappings.length > 0 &&
        mappings.every((mapping) => mapping.conversion_id && mapping.event);
    const createLabel =
        language?.buttons?.create_destination ?? "Create destination";
    const saveLabel = language?.buttons?.save ?? "Save";

    useHostFooterAction(
        isActive ? registerFooterAction : undefined,
        `${code}:form`,
        {
            label: createLabel,
            onSubmit: create,
            isDisabled: !canCreate,
            hidden: isMappingOpen,
        },
    );
    useHostFooterAction(
        isActive ? registerFooterAction : undefined,
        `${code}:mapping`,
        {
            label: saveLabel,
            onSubmit: saveMappings,
            isDisabled: !canSaveMappings,
            hidden: !isMappingOpen,
        },
    );

    return {
        setData,
        setIsAuthenticated,
        destinationId,
        conversions,
        events,
        autoMatchedEvents,
        mappings,
        setMappings,
        isMappingOpen,
        setIsMappingOpen,
        isMappingCompleted,
        resetOnTypeChange,
        create,
        saveMappings,
    };
};

export default useConversionMappingDestination;
